/**
 * TopNav — shared top navigation bar rendered on every authenticated
 * page of the dashboard.
 *
 * # Why this exists (UI/UX rule 6)
 *
 * Per `triage/CONTRIBUTING.md` §"Best practices at a glance — 5
 * dashboard + 5 job algorithm" rule 6: **every top-level section of
 * the dashboard must be reachable in one click from any other
 * top-level section, without bouncing through the index page.** The
 * old pattern ("← Home" link on each page header + card grid on
 * Home) cost two clicks + a page repaint for every cross-section
 * navigation. Operators triaging a live incident are the primary
 * user; every saved click is latency they don't spend reading
 * context back in after the round-trip.
 *
 * This component replaces the bare "← Home" link with a row of
 * section links, each carrying an icon + label + active-route
 * highlight. The brand link on the far left still points at `/`,
 * so the Home menu isn't DELETED — it just stops being the only
 * hub for navigation.
 *
 * # Layout
 *
 * ```
 *   [triage]  [Bundles] [Analyses] [Trends] [Activity] [Admin]     email   Sign out
 *   ^ brand   ^ primary-nav section links                          ^ session
 * ```
 *
 * - **Brand** (`triage / <environment>`, label from `/api/auth/me`) is
 *   a `<Link to="/">` — clicking the word "triage" always takes you
 *   home. Matches the dk2 dashboard pattern and operator muscle memory
 *   from every other web app.
 * - **Primary nav** is the five top-level authenticated sections:
 *   Bundles, Analyses, Trends, Activity, Admin. One link per card
 *   that exists on Home today — so any card you'd click from Home
 *   is equally reachable from every other page.
 * - **Session controls** on the right — signed-in email (hover for
 *   full address on truncation) + Sign out link. These match the
 *   old per-page header exactly so no muscle memory breaks.
 *
 * # Active-route highlighting
 *
 * `useLocation().pathname` + a per-link `isActive(path, current)`
 * helper derives which link is active. The active link gets a
 * filled background + bolder text; inactive links show a subtle
 * hover state. The highlight is visible even on monochrome
 * screenshots (per UI/UX rule 5 — color is never the only signal)
 * because the active link also carries an `aria-current="page"`
 * attribute AND a bolder font weight AND a small left-edge accent
 * stripe.
 *
 * # Keyboard + accessibility
 *
 * - `<nav aria-label="Primary">` wraps the section links so screen
 *   readers announce the landmark.
 * - Every link has a visible focus ring (Tailwind
 *   `focus-visible:outline-2 focus-visible:outline-blue-600`)
 *   matching the cards on Home.
 * - The active link carries `aria-current="page"`, the standard
 *   WAI-ARIA marker for "this is the page you're on."
 * - Tab order flows left-to-right: brand → section links →
 *   session controls. No `tabindex` overrides.
 *
 * # Responsive behavior
 *
 * The layout is a flex row that wraps at narrow viewports. Links
 * keep their full label on desktop + tablet; on phones (<640 px,
 * Tailwind `sm:`) the text-only labels stay visible because
 * operators on mobile Slack-to-dashboard flows still need to read
 * where they are — a hamburger-drawer pattern was considered + was
 * rejected because it adds a click back in, defeating the purpose
 * of the nav. The trade-off: labels wrap onto a second line on
 * very narrow phones, which is fine + expected.
 *
 * # Where it mounts
 *
 * Every top-level authenticated page (Home, Bundles, BundleDetail,
 * Analyses, AnalysisDetail, Duplicates, Trends, Activity, Admin,
 * AdminLlm) renders `<TopNav />` as the first child of its
 * `<header>` element. The per-page header retains its page
 * title + subtitle + page-specific status chips (e.g. "updating…"
 * on Bundles) but drops the old `← Home` / `← Bundles` back-link
 * because TopNav subsumes that affordance.
 *
 * Detail pages (BundleDetail, AnalysisDetail, Duplicates) STILL
 * render a breadcrumb-style back-link inside their page content
 * area ("← back to Bundles list") — that's separate from TopNav
 * because it's about returning to the parent *filtered view*,
 * not navigating to a *different section*. Keep the two affordances
 * distinct.
 */

import type { ReactNode } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";

import { cn } from "@/lib/utils";
import { useSession } from "@/lib/auth";
import { awaitingCount, queries } from "@/lib/api";
import { ReportLookupButton } from "./ReportLookupModal";
import { TimeZoneSwitch } from "./TimeZoneSwitch";
import { SlackLinkStatus } from "./SlackLinkStatus";

// ─── Section link shape ─────────────────────────────────────────────────────

/**
 * One entry in the primary nav. `to` is the destination; `matchPrefix`
 * is the path prefix that should highlight this link as active
 * (so `/bundles/:uuid` still highlights "Bundles", `/analyses/:uuid`
 * still highlights "Analyses", etc.).
 */
interface NavSection {
  label: string;
  to: string;
  /**
   * Prefix-match tokens (all must match from the start of the
   * current pathname). A link is active if the current pathname
   * starts with `matchPrefix` OR is exactly `to`.
   *
   * For `/trends?window=30d`, `to` is the full link including the
   * query string; `matchPrefix` is `/trends` so any trends URL
   * variant highlights the section.
   */
  matchPrefix: string;
  icon: ReactNode;
  /**
   * Plain-English short description. Shown as the `title=` tooltip
   * on the link so hovering confirms the destination without
   * navigating.
   */
  description: string;
}

// ─── Inline icon components (hand-transcribed Heroicons outline) ────────────
//
// Duplicated-by-design with Home.tsx — both files render a handful
// of icons inline rather than pulling in lucide-react or
// @heroicons/react to avoid a 50-KB+ dependency for five glyphs
// per file. When the count grows past ~15 unique icons, revisit.

function InboxIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      aria-hidden="true"
      focusable="false"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 12h5l1.5 3h5l1.5-3h5M4.5 4.5h15a1.5 1.5 0 0 1 1.5 1.5v12a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18V6a1.5 1.5 0 0 1 1.5-1.5Z"
      />
    </svg>
  );
}

// 2026-05-01: MagnifierIcon removed — the only SECTIONS entry using
// it (the "Analyses" nav entry) was deleted per operator directive
// ("Analyses page is redundant with Bundles + Trends"). The /analyses/:uuid
// detail route remains but isn't reachable from TopNav; drill-through from
// Bundles + Duplicates row clicks + Slack links is the entry path.

function LayersIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      aria-hidden="true"
      focusable="false"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 6 12 2.25 20.25 6 12 9.75 3.75 6Zm0 6L12 15.75 20.25 12M3.75 18 12 21.75 20.25 18"
      />
    </svg>
  );
}

function BellIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

function BarChartIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      aria-hidden="true"
      focusable="false"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 20h18M6 20V12m6 8V8m6 12v-6"
      />
    </svg>
  );
}

function GearIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      aria-hidden="true"
      focusable="false"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10.5 3.75h3l.4 2.1a7.5 7.5 0 0 1 2 1.15l2.05-.75 1.5 2.6-1.65 1.4a7.5 7.5 0 0 1 0 2.3l1.65 1.4-1.5 2.6-2.05-.75a7.5 7.5 0 0 1-2 1.15l-.4 2.1h-3l-.4-2.1a7.5 7.5 0 0 1-2-1.15l-2.05.75-1.5-2.6 1.65-1.4a7.5 7.5 0 0 1 0-2.3l-1.65-1.4 1.5-2.6 2.05.75a7.5 7.5 0 0 1 2-1.15l.4-2.1Z"
      />
      <circle cx="12" cy="12" r="2.75" />
    </svg>
  );
}

// TRIAG-312 — the /commits section's git-commit icon (a node on a line).
function CommitIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="3.25" />
      <path strokeLinecap="round" d="M3 12h5.75M15.25 12H21" />
    </svg>
  );
}

// TRIAG-281 — the /feedback section's chat-bubble icon.
// The chat's own icon: two overlapping bubbles, so it isn't Feedback's.
function ChatIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      aria-hidden="true"
      focusable="false"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 5.25h10.5a1.5 1.5 0 0 1 1.5 1.5v6a1.5 1.5 0 0 1-1.5 1.5H9l-3 2.25v-2.25H3.75a1.5 1.5 0 0 1-1.5-1.5v-6a1.5 1.5 0 0 1 1.5-1.5Z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 9.75h4.5a1.5 1.5 0 0 1 1.5 1.5v5.25a1.5 1.5 0 0 1-1.5 1.5h-1.5v2.25l-3-2.25h-3a1.5 1.5 0 0 1-1.5-1.5v-.75"
      />
    </svg>
  );
}

function ChatBubbleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      aria-hidden="true"
      focusable="false"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.5 5.25h15a1.5 1.5 0 0 1 1.5 1.5v9a1.5 1.5 0 0 1-1.5 1.5H12l-4.5 3.75V17.25h-3a1.5 1.5 0 0 1-1.5-1.5v-9a1.5 1.5 0 0 1 1.5-1.5Z"
      />
      <path strokeLinecap="round" d="M8 9.75h8M8 13h5" />
    </svg>
  );
}

// Users page (TRIAG-427) — the /users section's people icon (Heroicons `users` outline).
function UsersIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      aria-hidden="true"
      focusable="false"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
      />
    </svg>
  );
}

// Hardware page (TRIAG-429) — the /hardware section's chip icon (Heroicons `cpu-chip` outline).
function HardwareIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      aria-hidden="true"
      focusable="false"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 0 0 2.25-2.25V6.75a2.25 2.25 0 0 0-2.25-2.25H6.75A2.25 2.25 0 0 0 4.5 6.75v10.5a2.25 2.25 0 0 0 2.25 2.25Zm.75-12h9v9h-9v-9Z"
      />
    </svg>
  );
}

// Feature 05 slice 9 — the /remediations section's wrench icon.
function WrenchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      aria-hidden="true"
      focusable="false"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 6.75a5.25 5.25 0 0 1-7.1 4.92L7.5 18.07a2.1 2.1 0 1 1-2.97-2.97l6.4-6.4A5.25 5.25 0 0 1 17.6 2.3l-3.1 3.1 3.6 3.6 3.1-3.1c.51.85.8 1.85.8 2.85Z"
      />
    </svg>
  );
}

// ─── Section definitions ────────────────────────────────────────────────────
//
// Order matches the Home page card grid (Bundles → Analyses →
// Trends → Activity → Admin) so an operator moving between the
// two mental models doesn't see the sections in a different
// order in the two places.
//
// `matchPrefix` for detail pages: /bundles/:uuid should highlight
// "Bundles" (not a separate "Bundle Detail" tab). Same for
// /analyses/:uuid + /duplicates/:uuid. /admin/llm highlights
// "Admin" — one section covers both admin surfaces.

const SECTIONS: readonly NavSection[] = [
  {
    label: "Bundles",
    to: "/bundles",
    matchPrefix: "/bundles",
    icon: <InboxIcon className="h-4 w-4" />,
    description: "Every intake bundle — deduped, skipped, in-flight, completed",
  },
  // 2026-05-01: "Analyses" TopNav entry removed per operator
  // directive — the list page was redundant with /bundles +
  // /trends. The `/analyses/:uuid` detail route still exists
  // (Slack link targets, /bundles row clicks, /duplicates drill-
  // through all point at it); it's just not a destination
  // reachable from the nav bar anymore. Navigating TO a specific
  // analysis detail from the nav wasn't a workflow operators
  // actually used; they always enter via a list-page row click.
  {
    label: "Trends",
    // 2026-05-01: dropped the `?window=30d` query string. The
    // `/trends` page's default window was flipped from 30d to
    // 24h per the "primary operator interaction surface"
    // directive; the bare `/trends` URL now lands on the
    // 24h default which matches the operator's live-triage
    // mental model. Operators wanting a longer window use the
    // on-page segmented toggle (24h / 7d / 30d / 90d / 365d).
    // Keeping the bare path also means the Home-card "Trends"
    // link + TopNav "Trends" link + any Slack deep-link all
    // agree on the same canonical URL for the section's
    // default landing.
    to: "/trends",
    matchPrefix: "/trends",
    icon: <LayersIcon className="h-4 w-4" />,
    description: "Most common recurring error signatures",
  },
  {
    label: "Activity",
    to: "/activity",
    matchPrefix: "/activity",
    icon: <BarChartIcon className="h-4 w-4" />,
    description: "System activity over time — analyzed vs received",
  },
  {
    // Users page (TRIAG-427) — app users: search, profiles, subscriptions.
    label: "Users",
    to: "/users",
    matchPrefix: "/users",
    icon: <UsersIcon className="h-4 w-4" />,
    description: "Look up an app user and their reports over time",
  },
  {
    // Hardware page (TRIAG-429) — phone battery / heat / processor / memory.
    label: "Hardware",
    to: "/hardware",
    matchPrefix: "/hardware",
    icon: <HardwareIcon className="h-4 w-4" />,
    description: "Battery, heat, processor and memory across reports",
  },
  {
    // TRIAG-343 — the trend-alert record: what the watchdog raised,
    // when, and whether Slack received it. Session-visible (the Slack
    // mention targets land here — scenario 10, TREND_ALERTS_SCENARIOS.md).
    label: "Alerts",
    to: "/alerts",
    matchPrefix: "/alerts",
    icon: <BellIcon className="h-4 w-4" />,
    description:
      "Trend alerts — storms, new problems, slow burns, user write-ins",
  },
  {
    // TRIAG-312 — automated pre-flight review of new primary-branch
    // commits. Distinct workflow from the user-report surfaces above
    // (looks forward at code about to ship, not at filed bug reports).
    //
    // 2026-06 (operator directive): rendered inside the consolidated
    // "Fixes" dropdown, not as a top-level link — see FIXES_ITEMS.
    label: "Commits",
    to: "/commits",
    matchPrefix: "/commits",
    icon: <CommitIcon className="h-4 w-4" />,
    description: "Automated pre-flight review of new commits before they ship",
  },
  {
    // Galley #34714 (Chris's yes, #34699): a new page in the Fixes dropdown, after
    // Commits. Commits keeps its place, label and link; only this entry is added.
    label: "Recurring fixes",
    to: "/fixes/recurring",
    matchPrefix: "/fixes/recurring",
    icon: <WrenchIcon className="h-4 w-4" />,
    description: "The lines of code triage's patches keep asking to change, across reports",
  },
  {
    // Galley #36384 (the KICKOFF #36330): the problems behind those lines, grouped by root cause,
    // each with a suggested decision. Added last in the Fixes dropdown; Commits and Recurring
    // fixes keep their places, labels and links.
    label: "Problems",
    to: "/fixes/problems",
    matchPrefix: "/fixes/problems",
    icon: <WrenchIcon className="h-4 w-4" />,
    description:
      "The code problems triage's patches keep proposing to fix, each with a suggested decision",
  },
  {
    // TRIAG-281 — deduplicated #user-feedback issues. Sits between
    // Activity and Admin (appended late; Home card grid matches).
    label: "Feedback",
    to: "/feedback",
    matchPrefix: "/feedback",
    icon: <ChatBubbleIcon className="h-4 w-4" />,
    description:
      "What users report in the feedback channel — deduplicated into issues",
  },
  {
    // The owner's #20914: the chat is a top menu item, after Feedback. Only
    // the entry is added; every other item, and Home, stay as they were.
    label: "Chat",
    to: "/chat",
    matchPrefix: "/chat",
    icon: <ChatIcon className="h-4 w-4" />,
    description: "Ask triage a question, as you would in a Slack DM",
  },
  // Remediations (Feature 05) is retired from the dashboard (Galley #20162,
  // the owner's #20375 and #20595): no entry here, no Fixes menu item, and
  // no /remediations route; an old link falls to App's catch-all, Home.
] as const;

// ─── Fixes dropdown (operator-directed nav reorg) ───────────────────────────
//
// The two code-change surfaces — automated pre-flight commit review
// (/commits) and evidence-driven remediations (/remediations) — are
// grouped under one "Fixes" dropdown, following the Admin-dropdown
// consolidation pattern (same keyboard/Escape/aria behavior via the
// shared NavMenu below). Routes, pages, and deep links are untouched;
// only the nav grouping moved: `SECTIONS` remains the canonical
// registry of every non-admin section (tests + Home consume it); the
// component splits it into the top-level row and the Fixes group at
// render time. Every destination remains reachable in ≤ 2 clicks
// from anywhere (no lost pages).
//
// Label choice: "Fixes" (the operator-suggested default). The vision
// check (desktop + 390 px) confirmed it reads clearly next to the
// destination labels — no switch to the "Changes" fallback needed.

/** Paths grouped under the "Fixes" dropdown trigger. */
const FIXES_PATHS: readonly string[] = ["/commits", "/fixes/recurring", "/fixes/problems"];

/** Every Fixes destination — exported so tests pin "no lost pages". */
const FIXES_ITEMS: readonly NavSection[] = SECTIONS.filter((s) =>
  FIXES_PATHS.includes(s.to),
);

/** The sections still rendered as top-level links. */
const TOP_LEVEL_SECTIONS: readonly NavSection[] = SECTIONS.filter(
  (s) => !FIXES_PATHS.includes(s.to),
);

// ─── Trends dropdown (operator-directed nav reconciliation) ─────────────────
//
// The Trends section carries two in-page views (TRIAG-331's URL-synced
// PageTabs: "Oops Errors" default + "User Stories" ?tab=stories). The
// batched-UI-pass steering applies the same two-layer pattern the
// Fixes/Admin groups use: the top-level Trends slot becomes a NavMenu
// dropdown whose items deep-link into each tab. The in-page PageTabs on
// /trends are untouched — the dropdown is an ADDITIONAL entry point, not
// a replacement. `SECTIONS` still carries the canonical `/trends` entry
// (tests + Home consume it); the component swaps the Link for the
// dropdown at render time, in the same slot, so nav order + the Trends
// seen-counts badge are preserved.
//
// Item active-state is query-aware (see `navItemMatches`): bare
// `/trends` (any window/sort/filter, tab absent) highlights "Oops
// Errors"; `?tab=stories` and the `/trends/stories/:themeId` detail
// route highlight "User Stories".

/** Every Trends destination — exported so tests pin "no lost pages". */
const TRENDS_ITEMS: readonly NavSection[] = [
  {
    label: "Oops Errors",
    to: "/trends",
    matchPrefix: "/trends",
    icon: <LayersIcon className="h-4 w-4" />,
    description: "Recurring error families grouped by error signature",
  },
  {
    label: "User Stories",
    to: "/trends?tab=stories",
    // The shareable theme-detail route lives under /trends/stories/*;
    // it belongs to the User Stories view for highlighting purposes.
    matchPrefix: "/trends/stories",
    icon: <LayersIcon className="h-4 w-4" />,
    description:
      "What users are experiencing, grouped by narrative similarity — includes reports with no error signature",
  },
  {
    // TRIAG-342: the arrival-time shake-cluster lens. Same
    // deep-link + detail-route highlighting shape as User Stories.
    label: "Shake Reports",
    to: "/trends?tab=shakes",
    matchPrefix: "/trends/shakes",
    icon: <LayersIcon className="h-4 w-4" />,
    description:
      "Every mobile user report grouped by meaning within minutes of arrival — no analysis wait needed",
  },
] as const;

/**
 * Query-aware item matching for NavMenu items.
 *
 * An item matches when the pathname sits under its path (exact or
 * `/`-boundary prefix, same rule as `isSectionActive`) — additionally,
 * when the item's `to` carries a query string (the Trends "User
 * Stories" deep link), every `to` query param must be present with the
 * same value in the current search. Items may also claim a pathname
 * via their `matchPrefix` (so `/trends/stories/:id` matches the
 * stories item even though the deep link itself is `?tab=stories`).
 */
function navItemMatches(
  item: NavSection,
  pathname: string,
  search: string,
): boolean {
  const [toPath, toQuery] = item.to.split("?");
  const pathMatches = pathname === toPath || pathname.startsWith(`${toPath}/`);
  if (
    !toQuery &&
    (pathMatches ||
      pathname === item.matchPrefix ||
      pathname.startsWith(`${item.matchPrefix}/`))
  ) {
    return true;
  }
  if (!toQuery) return false;
  // matchPrefix claim (e.g. /trends/stories/:id → User Stories).
  if (
    pathname === item.matchPrefix ||
    pathname.startsWith(`${item.matchPrefix}/`)
  ) {
    return true;
  }
  if (!pathMatches) return false;
  const want = new URLSearchParams(toQuery);
  const have = new URLSearchParams(search);
  for (const [k, v] of want) {
    if (have.get(k) !== v) return false;
  }
  return true;
}

// ─── Admin dropdown (Feature 05 slice 9 / UX rec #5 consolidation) ─────────
//
// The /admin page family used to occupy one top-level "Admin" link
// whose sub-pages were only discoverable from the /admin header
// strip. Consolidating them into ONE dropdown (a) keeps every admin
// destination reachable in ≤ 2 clicks from anywhere (no lost pages),
// and (b) frees the top-level slot "Remediations" now occupies
// without widening the bar (the 390 px wrap fix, UX_EVALUATION
// rec #5 / UX_REVERIFICATION slice-9 scoping).

/** Every admin destination — exported so tests pin "no lost pages". */
const ADMIN_ITEMS: readonly NavSection[] = [
  {
    label: "Config",
    to: "/admin",
    matchPrefix: "/admin",
    icon: <GearIcon className="h-4 w-4" />,
    description: "People and access — priority users, admins, subscriptions",
  },
  {
    label: "LLM",
    to: "/admin/llm",
    matchPrefix: "/admin/llm",
    icon: <GearIcon className="h-4 w-4" />,
    description:
      "LLM observability — token rate, concurrency, per-call drill-down",
  },
  {
    label: "Usersnap",
    to: "/admin/usersnap",
    matchPrefix: "/admin/usersnap",
    icon: <GearIcon className="h-4 w-4" />,
    description: "Usersnap ingest health — config, credentials, KPIs",
  },
  {
    label: "Feedback ingest",
    to: "/admin/feedback",
    matchPrefix: "/admin/feedback",
    icon: <GearIcon className="h-4 w-4" />,
    description: "Slack feedback-ingest health — poller, backfill, backlog",
  },
  {
    label: "MCP",
    to: "/admin/mcp",
    matchPrefix: "/admin/mcp",
    icon: <GearIcon className="h-4 w-4" />,
    description: "MCP server setup + per-operator session management",
  },
  {
    label: "Page views",
    to: "/admin/page-views",
    matchPrefix: "/admin/page-views",
    icon: <GearIcon className="h-4 w-4" />,
    description: "Dashboard-usage analytics — per-user + per-route",
  },
] as const;

/**
 * A consolidated nav dropdown. One trigger (styled like a section
 * link, active when the current path is under any of the group's
 * destinations), a small menu of destinations. Closes on Escape,
 * click-outside, and item selection — the same dismissal contract
 * the modal components use.
 *
 * Extracted from the Admin dropdown (Feature 05 slice 9) when the
 * "Fixes" group was added, so both dropdowns share ONE implementation
 * and keyboard/Escape/aria parity is structural, not copied.
 */
function NavMenu({
  pathname,
  search = "",
  label,
  title,
  menuLabel,
  icon,
  items,
  badgeCount = 0,
}: {
  pathname: string;
  /** Current location.search — item matching is query-aware (Trends). */
  search?: string;
  label: string;
  title: string;
  menuLabel: string;
  icon: ReactNode;
  items: readonly NavSection[];
  /**
   * Optional seen-counts badge on the trigger (the Trends dropdown
   * inherits the top-level link's `trends_new` pill so consolidating
   * the slot doesn't silently lose the backlog signal). 0 = hidden.
   */
  badgeCount?: number;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();
  const active = items.some((item) => isSectionActive(item, pathname));
  const showBadge = badgeCount > 0;

  // Escape + click-outside dismissal, active only while open.
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    function onPointerDown(e: MouseEvent) {
      if (
        containerRef.current &&
        e.target instanceof Node &&
        !containerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative inline-flex">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-current={active ? "page" : undefined}
        title={title}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm sm:px-3",
          "transition-colors",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600",
          active
            ? "bg-blue-50 font-semibold text-blue-900 dark:bg-blue-950 dark:text-blue-100"
            : "font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-50",
        )}
      >
        <span
          className={cn(
            "flex h-4 w-4 items-center justify-center",
            active
              ? "text-blue-700 dark:text-blue-300"
              : "text-gray-500 dark:text-gray-500",
          )}
          aria-hidden="true"
        >
          {icon}
        </span>
        <span>{label}</span>
        {showBadge && (
          <span
            aria-hidden="true"
            className={cn(
              "ml-0.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[0.65rem] font-semibold leading-none",
              active
                ? "bg-blue-600 text-white dark:bg-blue-500 dark:text-white"
                : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
            )}
          >
            {badgeCount > 99 ? "99+" : badgeCount}
          </span>
        )}
        <span aria-hidden="true" className="text-[0.6rem]">
          ▾
        </span>
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={menuLabel}
          className="absolute left-0 top-full z-50 mt-1 min-w-[11rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900"
        >
          {items.map((item) => {
            // Longest-match wins: the item whose `to` best matches
            // the current location is the active one. Preserves the
            // Admin behavior (/admin/llm highlights LLM, not Config;
            // exactly /admin highlights Config), gives the Fixes
            // group /commits/:sha → Commits for free, and (via the
            // query-aware `navItemMatches`) lets the Trends group
            // distinguish ?tab=stories from the bare /trends URL.
            const bestMatch = items.reduce<string | null>((best, cand) => {
              if (!navItemMatches(cand, pathname, search)) return best;
              return best === null || cand.to.length > best.length
                ? cand.to
                : best;
            }, null);
            const itemActive = item.to === bestMatch;
            return (
              <Link
                key={item.to}
                to={item.to}
                role="menuitem"
                title={item.description}
                aria-current={itemActive ? "page" : undefined}
                onClick={() => setOpen(false)}
                className={cn(
                  "block px-3 py-1.5 text-sm",
                  "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-600",
                  itemActive
                    ? "bg-blue-50 font-semibold text-blue-900 dark:bg-blue-950 dark:text-blue-100"
                    : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800",
                )}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Duplicates is a detail-only section ────────────────────────────────────
//
// `/duplicates/:canonical_uuid` is reachable from Bundles rows +
// Analyses rows but has no list-level page today. The TopNav
// treats Duplicates as a sub-route of Bundles for active-route
// highlighting purposes — clicking into a duplicate family keeps
// "Bundles" highlighted so the operator knows they're drilled
// down from that section.

const DUPLICATES_PREFIX = "/duplicates";
const DUPLICATES_PARENT = "/bundles";

// ─── Active-route derivation ────────────────────────────────────────────────

/**
 * Return true if the given section is active for the current pathname.
 *
 * Rules:
 * - `/` (Home) is active only when the pathname is exactly `/` —
 *   no section is active on Home. TopNav on Home therefore shows
 *   no highlighted section, which matches operator expectation
 *   ("I'm on the menu, not inside a section").
 * - `/duplicates/*` counts as active for the Bundles section
 *   (see DUPLICATES_PARENT above).
 * - Otherwise, the pathname must start with `matchPrefix` + either
 *   end there or continue with a `/` (so `/bundles-x` doesn't
 *   falsely match `/bundles`).
 */
function isSectionActive(section: NavSection, pathname: string): boolean {
  if (
    pathname.startsWith(DUPLICATES_PREFIX) &&
    section.matchPrefix === DUPLICATES_PARENT
  ) {
    return true;
  }
  if (pathname === section.matchPrefix) return true;
  return pathname.startsWith(`${section.matchPrefix}/`);
}

// ─── The component ──────────────────────────────────────────────────────────

/**
 * Props — none today. The component reads session info from the
 * shared `useSession()` hook so parents don't have to thread it
 * through. Keeps the call site in every page header trivial:
 * `<TopNav />`.
 *
 * Future: a `compact?: boolean` prop could hide section labels
 * (icons only) on very narrow viewports. Not needed today —
 * the wrap-to-second-line fallback handles phones adequately and
 * a compact mode would lose the color-is-never-the-only-signal
 * property (rule 5).
 */
/** Galley #26408: the Operator section link, styled like every other
 *  top-level section link (color + weight + aria-current when active). */
function OperatorLink({ active }: { active: boolean }) {
  return (
    <Link
      to="/operator"
      title="Operator: feedback about triage, kept from chat DMs"
      aria-current={active ? "page" : undefined}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm sm:px-3 sm:py-1.5",
        "transition-colors",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600",
        active
          ? "bg-blue-50 font-semibold text-blue-900 dark:bg-blue-950 dark:text-blue-100"
          : "font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-50",
      )}
    >
      <span>Operator</span>
    </Link>
  );
}

export function TopNav() {
  const location = useLocation();
  const pathname = location.pathname;
  const { data: session } = useSession();
  const email = session?.email ?? "";

  // Commit B (2026-05-01) — per-user seen-counts badge on the
  // Bundles + Trends section links. Answers the "overwhelmed by
  // new stuff" operator directive at its highest leverage point:
  // operator deep in a detail page or on a different section
  // sees backlog growth without having to navigate back to the
  // list to learn about it.
  //
  // Both `bundles_new` and `trends_new` are real aggregates as of
  // Step 2 (2026-05-01 follow-up). Pre-Step-2 the server
  // hardcoded `trends_new` to 0 and the Trends badge never lit;
  // the SPA's defensive `?? 0` posture below (inherited from the
  // v1 drop-in anticipation) still works post-Step-2 — a real
  // positive count simply flips the `showBadge` gate and the
  // pill renders. No SPA change was required to enable the
  // Trends badge; the backend started returning the real
  // predicate count (signature-scoped baseline-at-first-visit +
  // relight-grace) and the badge lit up.
  //
  // `useQuery` wrapping `queries.seenCounts()` handles polling
  // + stale-time + refetch-on-focus + retry-0 (silent degrade
  // on 503 when DATABASE_URL unset). The data is `undefined`
  // before first fetch + on error — the `countByTo` map below
  // treats missing data as "no badges render" which is the
  // calm-onboarding default.
  const { data: seenCounts } = useQuery(queries.seenCounts());
  const countByTo: Record<string, number> = {
    "/bundles": seenCounts?.bundles_new ?? 0,
    "/trends": seenCounts?.trends_new ?? 0,
  };

  // Migration 026 (2026-05-12) — awaiting-action count badge. A
  // separate query (not part of `seenCounts`) because the
  // semantics are different: `seenCounts` tracks "new since YOUR
  // last visit" (per-user), while this badge tracks "awaiting
  // operator action" (global). The badge appears next to the
  // Bundles section link with a `⚠️` glyph + integer count when
  // > 0; hidden when 0 (clean top-nav default).
  //
  // 30s refetch interval matches the server's 10s `Cache-Control:
  // private, max-age=10` so we never beat the cache more than 3x
  // per minute. The endpoint is index-only on `idx_bl_manual_active`;
  // sub-millisecond DB cost. Falls back silently to 0 on error
  // (the calm-onboarding default — a network blip shouldn't make
  // the operator's nav surface scream).
  const { data: awaitingData } = useQuery({
    queryKey: ["awaiting-count"],
    queryFn: awaitingCount,
    refetchInterval: 30_000,
    staleTime: 25_000,
    retry: 0,
  });
  const awaitingCountValue = awaitingData?.count ?? 0;
  const showAwaitingBadge = awaitingCountValue > 0;

  return (
    <div className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2 sm:gap-x-4 sm:gap-y-2 sm:px-6 sm:py-3">
        {/* Brand — always links home */}
        <Link
          to="/"
          className="flex items-baseline gap-2 text-sm font-semibold tracking-tight text-gray-900 hover:text-blue-700 focus-visible:rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:text-gray-50 dark:hover:text-blue-400"
          aria-label="triage — home"
        >
          <span className="text-base">triage</span>
          <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
            {session?.environment ?? "…"}
          </span>
        </Link>

        {/*
          Primary section nav. A <nav> landmark so screen readers
          group these links under "Primary navigation."
        */}
        <nav
          aria-label="Primary"
          // `max-sm:basis-full` drops the section links onto their own
          // full-width row(s) below the brand on phones. Without it the
          // nav is squeezed into the leftover column between the brand
          // and the session controls at 390 px and every link wraps onto
          // its own line — a seven-row nav (slice-9b vision finding).
          className="flex flex-1 flex-wrap items-center gap-0.5 max-sm:order-last max-sm:basis-full sm:gap-1"
        >
          {TOP_LEVEL_SECTIONS.map((section) => {
            // Operator-directed nav reconciliation (batched UI pass):
            // the Trends slot renders as a NavMenu dropdown (Oops
            // Errors → /trends, User Stories → /trends?tab=stories)
            // instead of a plain link — same slot, same order, badge
            // preserved on the trigger. The /trends PageTabs remain
            // the in-page layer of the two-layer pattern.
            if (section.to === "/trends") {
              return (
                <NavMenu
                  key={section.to}
                  pathname={pathname}
                  search={location.search}
                  label="Trends"
                  title="Trends — recurring error signatures + user story themes"
                  menuLabel="Trends views"
                  icon={section.icon}
                  items={TRENDS_ITEMS}
                  badgeCount={countByTo[section.to] ?? 0}
                />
              );
            }
            const active = isSectionActive(section, pathname);
            // Commit B — seen-counts badge. Only renders when
            // the count is strictly > 0; zero-state is silent
            // (matches the "empty when zero" UX decision —
            // showing "0 new" would be noise, not signal).
            // Only Bundles + Trends carry counts in v1; every
            // other section's `countByTo[section.to]` is
            // undefined → falsy → no badge.
            const newCount = countByTo[section.to] ?? 0;
            const showBadge = newCount > 0;
            // TRIAG-64 (2026-05-21): the awaiting-action badge
            // is rendered as a SEPARATE Link sibling next to the
            // section Link so clicking the badge navigates to the
            // pre-filtered awaiting subset, while clicking the
            // section's icon/label navigates to the full /bundles
            // list. HTML doesn't allow nesting <a> inside <a>;
            // separating them is the only correct shape.
            const awaitingBadgeVisible =
              section.to === "/bundles" && showAwaitingBadge;
            return (
              <span key={section.to} className="inline-flex items-center">
                <Link
                  to={section.to}
                  title={
                    showBadge
                      ? `${section.description} — ${newCount} new since your last visit`
                      : section.description
                  }
                  aria-current={active ? "page" : undefined}
                  aria-label={
                    showBadge ? `${section.label}, ${newCount} new` : undefined
                  }
                  className={cn(
                    // Base layout. Tighter padding below `sm` (the 390 px
                    // wrap fix, UX rec #5): six links + the Admin menu fit
                    // two rows on a 390 px phone instead of four.
                    "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm sm:px-3 sm:py-1.5",
                    "transition-colors",
                    // Focus ring — always visible on keyboard focus
                    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600",
                    // Active vs inactive — three independent signals
                    // (per UI/UX rule 5: color is never the only one).
                    // (a) color: blue-tinted background + darker text
                    // (b) weight: semibold vs medium
                    // (c) glyph: icon stroke width effectively darker
                    //     because color contrast rises, AND aria-current
                    //     marker is read by screen readers.
                    active
                      ? "bg-blue-50 font-semibold text-blue-900 dark:bg-blue-950 dark:text-blue-100"
                      : "font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-50",
                  )}
                >
                  <span
                    className={cn(
                      "flex h-4 w-4 items-center justify-center",
                      active
                        ? "text-blue-700 dark:text-blue-300"
                        : "text-gray-500 dark:text-gray-500",
                    )}
                    aria-hidden="true"
                  >
                    {section.icon}
                  </span>
                  <span>{section.label}</span>
                  {/*
                    Commit B seen-counts badge. Small blue pill
                    with the integer count. `aria-hidden` because
                    the parent Link already carries an aria-label
                    surfacing the count semantically — duplicating
                    on the badge would make screen readers
                    announce "Bundles, 37 new, 37" which is noise.
                    Color + glyph (the "new" word in the title
                    attr / aria-label) + label (the number) satisfy
                    the color-is-never-the-only-signal rule 5.
                  */}
                  {showBadge && (
                    <span
                      aria-hidden="true"
                      className={cn(
                        "ml-0.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[0.65rem] font-semibold leading-none",
                        active
                          ? "bg-blue-600 text-white dark:bg-blue-500 dark:text-white"
                          : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
                      )}
                    >
                      {newCount > 99 ? "99+" : newCount}
                    </span>
                  )}
                </Link>
                {/*
                  TRIAG-64 (2026-05-21): awaiting-action badge as a
                  separate <Link>. Renders ONLY on the Bundles
                  section (gated to `awaitingBadgeVisible`) when the
                  count is > 0. Clicking it navigates to the
                  pre-filtered awaiting subset:
                  `/bundles?intake_policies=manual`. Pre-fix the
                  badge was a non-clickable span inside the section
                  Link, and the tooltip asked operators to manually
                  filter — friction during live incidents (Prakash
                  flagged it 2026-05-21). The pre-filtered URL
                  composes with the default `dismissed=active` on
                  the SPA side so the table renders only bundles
                  awaiting an operator decision (intake_policy =
                  'manual' AND analysis_started_at IS NULL AND
                  dismissed_at IS NULL — matches the
                  `idx_bl_manual_active` partial index from
                  migration 026).
                  Amber chip with `⚠` glyph matches the Auto-off
                  chip on individual `/bundles` rows + the
                  awaiting-action banner on `/bundles/:uuid` for
                  consistent visual vocabulary across surfaces.
                  `aria-label` carries the count semantically so
                  screen readers announce "6 awaiting operator
                  action, opens filtered list" without duplicating
                  with the seen-counts badge.
                */}
                {awaitingBadgeVisible && (
                  <Link
                    to="/bundles?intake_policies=manual"
                    aria-label={`${awaitingCountValue} awaiting operator action, opens filtered list`}
                    title={`View ${awaitingCountValue} bundle${
                      awaitingCountValue === 1 ? "" : "s"
                    } awaiting operator action`}
                    className="ml-1 inline-flex min-w-[1.25rem] items-center justify-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[0.65rem] font-semibold leading-none text-amber-900 transition-colors hover:bg-amber-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600 dark:bg-amber-900 dark:text-amber-100 dark:hover:bg-amber-800"
                  >
                    <span aria-hidden="true">⚠</span>
                    <span>
                      {awaitingCountValue > 99 ? "99+" : awaitingCountValue}
                    </span>
                  </Link>
                )}
              </span>
            );
          })}
          {/*
            Operator-directed nav reorg — the consolidated Fixes
            dropdown. One trigger covering the two code-change
            surfaces (Commits, Remediations) — both destinations the
            old top-level links reached are still reachable, now from
            one grouped trigger. Routes/deep links unchanged.
          */}
          <NavMenu
            pathname={pathname}
            label="Fixes"
            title="Fixes — commit pre-flight review and recurring fixes"
            menuLabel="Fixes pages"
            icon={<WrenchIcon className="h-4 w-4" />}
            items={FIXES_ITEMS}
          />
          {/*
            Feature 05 slice 9 — the consolidated Admin dropdown. One
            trigger covering the whole /admin/* family (Config, LLM,
            Usersnap, Feedback ingest, MCP, Page views) — every
            destination the old top-level link + /admin sub-nav
            reached is still reachable, now from anywhere.
          */}
          <NavMenu
            pathname={pathname}
            label="Admin"
            title="Admin — runtime configuration + observability pages"
            menuLabel="Admin pages"
            icon={<GearIcon className="h-4 w-4" />}
            items={ADMIN_ITEMS}
          />
          {/*
            Galley #26408 (the owner's #26840): the Operator section,
            last in the row, only for a session the server marks
            `is_operator`. Everyone else's nav is exactly as before.
            UX gate only: /api/operator/* re-checks the role.
          */}
          {session?.is_operator === true && (
            <OperatorLink active={pathname.startsWith("/operator")} />
          )}
        </nav>

        {/*
          TRIAG-252: paste-box lookup. Global affordance (rendered
          here in the shared nav so it's one click from every page,
          including Home + /trends where the originating Slack
          request started). Opens a modal; doesn't navigate.
        */}
        <ReportLookupButton />

        {/* Galley #21943: the zone every time is shown in, always
            visible, and the switch between your time zone and UTC. On
            phones it sits here; from 640 px it is a line in the session
            column below, so no nav item moves to make room. */}
        <div className="sm:hidden">
          <TimeZoneSwitch variant="button" />
        </div>

        {/*
          Session controls — email + Sign out. Mirrors the shape
          every old per-page header used, so muscle memory is
          preserved.
        */}
        <div className="flex items-center gap-4">
          {email && (
            // `relative`: SlackLinkStatus's how-to panel hangs from this
            // column's bottom edge, so open it covers nothing in the bar
            // (Look up, Subscriptions, Times), only the page under it.
            <div className="relative hidden max-w-[16rem] flex-col items-end sm:flex">
              <span
                className="max-w-full truncate text-xs text-gray-600 dark:text-gray-400"
                title={email}
              >
                {email}
              </span>
              {/* `/triage link`: the linked Slack account, or "not linked". */}
              <SlackLinkStatus slack={session?.slack} />
              {/* Users page (TRIAG-427): personal settings sit next to the Slack
                  link. Here, not inside SlackLinkStatus (rendered without a
                  router in its tests). Hidden below 640 px with this block;
                  the Users page header carries the phone link. */}
              <Link
                to="/subscriptions"
                className="text-[11px] leading-tight text-gray-500 underline decoration-dotted underline-offset-2 hover:text-gray-800 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gray-500 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Subscriptions
              </Link>
              {/* Galley #21943: the time-zone switch, from 640 px. */}
              <TimeZoneSwitch variant="line" />
            </div>
          )}
          {/*
            Logout is a <form method="post"> (not a plain <a>)
            because the backend route is registered as
            `post(auth_google::logout)` — a GET request to
            `/auth/google/logout` returns 405 Method Not Allowed
            and the session cookie is never cleared. A plain
            anchor issues a GET, so the old shape appeared to
            "succeed" (page navigated, operator thought they
            were signed out) but the `triage_session` cookie
            kept validating for up to 24 hours.

            The form POST matches the backend's method + follows
            the `Redirect::to("/login")` response automatically,
            which keeps the muscle-memory click-and-go UX of the
            old anchor.

            Not a React Router primitive — this is a real
            same-origin POST that the browser follows to a
            backend-set-cookie response, which is a full-page
            navigation outside the SPA's routing scope
            (same rationale as the sign-in link on Login.tsx).

            SameSite=Lax on the session cookie blocks cross-
            site POSTs from carrying the cookie, so this form
            is not a logout-CSRF vector from another origin —
            an attacker-controlled page can't force a
            cross-site POST here with the session cookie
            attached.

            The <button> is styled to visually match the
            previous anchor (link-style text, underline on
            hover, focus-visible ring) so operators see zero
            UX change beyond "logout now actually logs you out."
          */}
          <form action="/auth/google/logout" method="post" className="inline">
            <button
              type="submit"
              className="cursor-pointer border-none bg-transparent p-0 text-xs text-gray-600 underline-offset-4 hover:text-gray-900 hover:underline focus-visible:rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:text-gray-400 dark:hover:text-gray-50"
            >
              Sign out
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Exports ────────────────────────────────────────────────────────────────
//
// The active-route helper + the SECTIONS list are exported so
// tests (and future pages that want a "you are here" breadcrumb
// keyed off the same data) can consume them without re-deriving.

// TRIAG-46: nav constants + helper colocated with TopNav; tests + future
// breadcrumb pages import these. HMR degradation acceptable per
// eslint.config.js posture.
// eslint-disable-next-line react-refresh/only-export-components -- TRIAG-46: see comment above.
export { SECTIONS, ADMIN_ITEMS, FIXES_ITEMS, TRENDS_ITEMS, isSectionActive };
export type { NavSection };
